<script setup lang="ts">
import { Paperclip, X } from '@lucide/vue'
import { mailAttachmentLimitsSentence } from '@zerohuman/shared'
import { Button } from '@zerohuman/ui'
import { ref } from 'vue'

/** The files a mail being written will carry: chosen from the device, each removable before it is sent. */
const files = defineModel<File[]>({ required: true })
defineProps<{ disabled?: boolean }>()

const input = ref<HTMLInputElement | null>(null)

function picked(event: Event): void {
    const target = event.target as HTMLInputElement
    files.value = [...files.value, ...Array.from(target.files ?? [])]
    // The same file may be chosen again after it is removed.
    target.value = ''
}

function drop(index: number): void {
    files.value = files.value.filter((_, at) => at !== index)
}
</script>

<template>
	<div class="space-y-1" data-testid="mail-attachments">
		<div class="flex flex-wrap items-center gap-2">
			<Button type="button" size="sm" variant="outline" :disabled="disabled" data-testid="mail-attach" @click="input?.click()">
				<Paperclip class="size-3.5" aria-hidden="true" />
				Attach files
			</Button>
			<span class="text-muted-foreground text-xs">{{ mailAttachmentLimitsSentence() }}</span>
			<input ref="input" type="file" multiple class="sr-only" tabindex="-1" aria-label="Attach files" data-testid="mail-attach-input" @change="picked" />
		</div>
		<ul v-if="files.length" class="flex flex-wrap gap-2 text-xs">
			<li v-for="(file, index) in files" :key="`${file.name}-${index}`" class="bg-muted flex items-center gap-1 rounded px-2 py-1" data-attached-file>
				{{ file.name }}
				<button type="button" class="text-muted-foreground hover:text-foreground" :aria-label="`Remove ${file.name}`" :disabled="disabled" @click="drop(index)">
					<X class="size-3" aria-hidden="true" />
				</button>
			</li>
		</ul>
	</div>
</template>
